<template>
  <section class="flex flex-col h-full">
    <!-- Draft bar -->
    <div
      class="flex items-center justify-between gap-3 px-5 py-2.5 bg-brand-teal-dark text-white flex-wrap"
    >
      <div class="flex items-center gap-2 text-[13px]">
        <span class="w-2 h-2 rounded-full bg-brand-yellow"></span>
        <span>{{ draftLabel }}</span>
      </div>
      <div class="flex gap-2">
        <button
          class="btn btn-sm bg-white bg-opacity-10 text-white border-white border-opacity-20 hover:bg-white hover:bg-opacity-20"
          @click="go('library')"
        >
          <arrow-left-icon class="w-3.5 h-3.5" /> Back to library
        </button>
        <button
          class="btn btn-sm bg-white bg-opacity-10 text-white border-white border-opacity-20 hover:bg-white hover:bg-opacity-20"
          :disabled="!canEdit"
          :title="canEdit ? '' : 'Viewers cannot save drafts'"
          @click="saveDraft"
        >
          <span>{{ editorSaved ? "Saved ✓" : "Save Draft" }}</span>
        </button>
        <button
          class="btn btn-sm bg-brand-yellow text-brand-teal-dark border-brand-yellow font-semibold"
          :disabled="!canEdit"
          :title="canEdit ? '' : 'Viewers cannot submit drafts for review'"
          @click="modal = 'review'"
        >
          Submit for Review →
        </button>
      </div>
    </div>

    <!-- Editor topbar -->
    <div
      class="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-dark-border bg-white bg-opacity-70 dark:bg-dark-surface dark:bg-opacity-60 flex-wrap gap-2"
    >
      <h2 class="font-heading font-bold text-sm">{{ editorTitle }}</h2>
      <div class="flex gap-2">
        <span class="badge badge-live">● Live</span>
        <span class="badge badge-teal">Callia Core</span>
      </div>
    </div>

    <!-- Split -->
    <div
      class="grid grid-cols-1 md:grid-cols-[280px_1fr] flex-1 overflow-hidden"
    >
      <!-- LEFT: variation list -->
      <div
        class="border-r border-gray-200 dark:border-dark-border overflow-y-auto app-scroll bg-gray-50 bg-opacity-50 dark:bg-dark-surface dark:bg-opacity-40"
      >
        <!-- Message type switcher -->
        <div
          class="p-3.5 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card"
        >
          <div
            class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted mb-2"
          >
            Message type
          </div>
          <div class="flex flex-col gap-1">
            <button
              v-for="t in messageTypes"
              :key="t.key"
              class="text-left px-2.5 py-1.5 rounded-lg text-[13px] font-medium border transition-colors"
              :class="
                msgType === t.key
                  ? 'bg-brand-light-blue text-brand-teal-dark border-brand-teal border-opacity-30 dark:bg-brand-teal dark:bg-opacity-20 dark:text-brand-light-blue'
                  : 'text-gray-500 dark:text-dark-text-muted border-transparent hover:bg-gray-100 dark:hover:bg-dark-card-hover'
              "
              @click="switchMsgType(t.key)"
            >
              <span>{{ t.label }}</span>
              <span class="text-[11px] font-normal opacity-70">{{
                "· " + t.sub
              }}</span>
            </button>
          </div>
        </div>

        <!-- Variations (only for types that have them) -->
        <div v-show="hasVariations">
          <div
            class="px-3.5 pt-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
          >
            Variations
          </div>
          <div
            v-for="b in buildings"
            :key="b.groupId"
            class="border-b border-gray-200 dark:border-dark-border"
          >
            <button
              class="w-full flex items-center gap-2 px-3.5 py-2.5 text-[13px] font-medium hover:bg-gray-100 dark:hover:bg-dark-card-hover"
              @click="toggleGroup(b.groupId)"
            >
              <span>{{ b.icon + " " + b.key }}</span>
              <span
                class="ml-auto text-[11px] text-gray-400 dark:text-dark-text-muted"
                >6 variations</span
              >
              <chevron-down-icon
                class="w-4 h-4 transition-transform"
                :class="openGroups[b.groupId] ? '' : '-rotate-90'"
              />
            </button>
            <div v-show="openGroups[b.groupId]">
              <button
                v-for="v in variationsFor(b.key)"
                :key="v.key"
                class="w-full flex items-center justify-between gap-2 pl-7 pr-3.5 py-2 text-[13px] border-l-2 transition-colors"
                :class="
                  isVarActive(v.building, v.timing, v.weather)
                    ? 'border-brand-teal bg-brand-light-blue bg-opacity-40 dark:bg-brand-teal dark:bg-opacity-15 text-brand-teal-dark dark:text-brand-light-blue'
                    : 'border-transparent text-gray-600 dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-card-hover'
                "
                @click="selectVariation(v.building, v.timing, v.weather)"
              >
                <span>{{ v.timing + " · " + v.weather }}</span>
                <span
                  class="badge text-[10px]"
                  :class="v.status === 'draft' ? 'badge-draft' : 'badge-live'"
                  >{{ v.status === "draft" ? "Draft" : "Live" }}</span
                >
              </button>
            </div>
          </div>
        </div>

        <!-- Single-message notice -->
        <div v-show="!hasVariations" class="p-4">
          <div
            class="rounded-lg border border-brand-teal border-opacity-20 bg-brand-light-blue bg-opacity-50 dark:bg-brand-teal dark:bg-opacity-10 p-3.5 text-[13px] text-brand-teal-dark dark:text-brand-light-blue leading-relaxed"
          >
            This message type has <strong>one version</strong> — no variations
            by building type or weather. Edit the single message on the right.
          </div>
        </div>

        <!-- Version history -->
        <div class="border-t border-gray-200 dark:border-dark-border p-3.5">
          <div
            class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted mb-2.5"
          >
            Version history
          </div>
          <div class="space-y-3">
            <div
              v-for="(ver, i) in editorVersions"
              :key="i"
              class="flex gap-2.5"
            >
              <div
                class="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                :class="
                  ver.current
                    ? 'bg-brand-teal'
                    : 'bg-gray-300 dark:bg-dark-border'
                "
              ></div>
              <div>
                <div class="text-[12.5px] font-medium">{{ ver.user }}</div>
                <div
                  class="text-[11px] text-gray-400 dark:text-dark-text-muted"
                >
                  {{ ver.meta }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: editor -->
      <div class="overflow-y-auto app-scroll p-6">
        <!-- Variation context -->
        <div class="flex items-center gap-2 mb-4 flex-wrap">
          <span class="text-[13px] font-semibold">{{ variationTitle }}</span>
          <span
            v-show="hasVariations"
            class="badge"
            :class="editorVarIsDraft ? 'badge-draft' : 'badge-live'"
            >{{ editorVarIsDraft ? "◌ Draft" : "● Live" }}</span
          >
          <div class="ml-auto">
            <button
              v-show="hasVariations"
              class="btn btn-ghost btn-sm"
              @click="copyToSimilar"
            >
              <span>{{
                editorCopied ? "Copied ✓" : "Copy to similar variations"
              }}</span>
            </button>
          </div>
        </div>

        <!-- Timing -->
        <div class="card mb-4">
          <div class="card-header"><div class="card-title">Timing</div></div>
          <div class="card-body grid sm:grid-cols-2 gap-4">
            <div>
              <label class="field-label">Trigger</label>
              <select class="field">
                <option selected>Day before delivery · evening</option>
                <option>Day of delivery · morning (8:00 AM)</option>
                <option>At driver assignment</option>
                <option>Custom time</option>
              </select>
            </div>
            <div>
              <label class="field-label">Send at</label>
              <input type="time" class="field" value="08:00" />
            </div>
          </div>
        </div>

        <!-- Message content -->
        <div class="card mb-4">
          <div class="card-header">
            <div class="card-title">Message content</div>
            <span
              class="text-[12px] text-gray-500 dark:text-dark-text-muted"
              :class="segCountClass"
              >{{ charCountLabel }}</span
            >
          </div>
          <div class="card-body">
            <textarea
              class="field min-h-[130px] font-mono text-[13px]"
              ref="editorTextarea"
              v-model="msgContent"
              aria-label="Message content"
            ></textarea>

            <!-- Segment panel -->
            <div class="mt-3">
              <div
                class="flex items-center justify-between flex-wrap gap-2 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface"
              >
                <div class="flex gap-5 items-center flex-wrap">
                  <div>
                    <div
                      class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
                    >
                      Characters
                    </div>
                    <div class="text-lg font-semibold font-mono">
                      {{ segInfo.chars }}
                    </div>
                  </div>
                  <div>
                    <div
                      class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
                    >
                      Segments
                    </div>
                    <div
                      class="text-lg font-semibold font-mono"
                      :class="segCountClass"
                    >
                      {{ segInfo.segments }}
                    </div>
                  </div>
                  <div>
                    <div
                      class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
                    >
                      Encoding
                    </div>
                    <div class="mt-0.5">
                      <span class="badge badge-live">{{
                        segInfo.encoding
                      }}</span>
                    </div>
                  </div>
                  <div>
                    <div
                      class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-text-muted"
                    >
                      Remaining
                    </div>
                    <div
                      class="text-lg font-semibold font-mono"
                      :class="segRemainingClass"
                    >
                      {{ segInfo.remaining }}
                    </div>
                  </div>
                </div>
                <a
                  href="https://twiliodeved.github.io/message-segment-calculator/"
                  target="_blank"
                  rel="noopener"
                  class="text-[11.5px] text-brand-teal no-underline whitespace-nowrap"
                  >Check in Twilio calculator ↗</a
                >
              </div>

              <div
                v-for="(s, i) in segSuggestions"
                :key="i"
                class="mt-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900 dark:bg-opacity-20 border border-amber-300 dark:border-amber-700 dark:border-opacity-40 text-[12.5px] text-amber-800 dark:text-amber-300 leading-relaxed"
              >
                {{ s }}
              </div>
            </div>

            <!-- Insert variable -->
            <div class="mt-3.5">
              <div class="field-label mb-2">Insert variable</div>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="v in editorVars"
                  :key="v"
                  class="btn btn-outline btn-xs font-mono"
                  @click="insertVar(v)"
                >
                  {{ v }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Diff -->
        <div v-show="hasVariations" class="card">
          <div class="card-header">
            <div class="card-title">Changes from live version</div>
            <span class="badge badge-draft">1 change</span>
          </div>
          <div class="card-body space-y-1">
            <div
              v-for="(d, i) in diffLines"
              :key="i"
              class="diff-line"
              :class="d.kind === 'removed' ? 'diff-removed' : 'diff-added'"
            >
              {{ (d.kind === "removed" ? "- " : "+ ") + d.text }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <review-modal v-if="modal === 'review'" @close="modal = null" />
  </section>
</template>

<script setup>
import { ref, getCurrentInstance } from "vue";
import { ArrowLeftIcon, ChevronDownIcon } from "vue-feather-icons";
import { useSmsEditor } from "./composables/useSmsEditor";
import { useSmsTier } from "./composables/useSmsTier";
import ReviewModal from "./components/ReviewModal.vue";

const {
  msgType,
  msgContent,
  openGroups,
  editorSaved,
  editorCopied,
  editorTextarea,
  messageTypes,
  buildings,
  editorVars,
  editorVersions,
  variationsFor,
  hasVariations,
  editorTitle,
  variationTitle,
  draftLabel,
  segInfo,
  segSuggestions,
  segCountClass,
  segRemainingClass,
  charCountLabel,
  diffLines,
  editorVarIsDraft,
  isVarActive,
  selectVariation,
  switchMsgType,
  toggleGroup,
  insertVar,
  saveDraft,
  copyToSimilar
} = useSmsEditor();

const { canEdit } = useSmsTier();

const modal = ref(null);

const { proxy } = getCurrentInstance();
function go(route) {
  proxy.$router.push(`/secure/sms-portal/${route}`);
}
</script>
